// Teacher-facing quiz creation + exam-mode (JEE/NEET) settings + publish.
// Standalone route (registered directly in App.js at /teacher/quizzes,
// sibling to the /teacher/* catch-all) rather than wedged into the large
// TeacherDashboard.js monolith — this is the only teacher-facing surface
// for the quizzes/quiz-attempts system, which previously had ZERO
// frontend caller anywhere (POST /api/ai/generate-quiz was unreachable),
// so no quiz could ever reach a student's My Quizzes list.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { ClipboardList, Sparkles, Award, ArrowLeft, Loader2, AlertTriangle } from './components/DeskeyIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXAM_MODES = [
  { value: '', label: 'Regular quiz (no negative marking)' },
  { value: 'JEE_MAIN', label: 'JEE Main' },
  { value: 'JEE_ADVANCED', label: 'JEE Advanced' },
  { value: 'NEET', label: 'NEET' },
  { value: 'SAT', label: 'SAT (no negative marking)' },
  { value: 'ITO', label: 'ITO (no negative marking)' },
];
// Matches backend DEFAULT_NEGATIVE_MARKS_BY_EXAM's deliberate omissions
// (quiz_exam_settings_routes.py) -- these exams score wrong answers as 0,
// same as unanswered.
const NO_NEGATIVE_MARKING_EXAMS = new Set(['SAT', 'ITO']);
const NEGATIVE_MARKING_TYPES = new Set(['mcq', 'assertion_reason']);
// Exam-mode scoring only grades these types (exam_quiz_scoring.py's
// GRADABLE_TYPES_EXAM_MODE) — generate-quiz's AI output isn't restricted
// to them (it also produces short_answer/long_answer), so a question of
// any other type silently never shows up in the student's exam-mode
// attempt at all once exam_mode is on.
const EXAM_MODE_GRADABLE_TYPES = new Set(['mcq', 'numerical', 'assertion_reason']);

export default function TeacherQuizGenerator() {
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ topic: '', content: '', difficulty: 'medium', count: 5 });
  const [generating, setGenerating] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [classId, setClassId] = useState('');
  const [examMode, setExamMode] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  useEffect(() => {
    axios.get(`${API}/classes/my-assigned`)
      .then(res => setClasses(res.data.classes || []))
      .catch(() => toast.error('Could not load your classes'));
  }, []);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error('Enter a topic'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/generate-quiz`, form);
      setQuiz(res.data);
      toast.success(`${(res.data.questions || []).length} questions generated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate quiz');
    } finally {
      setGenerating(false);
    }
  };

  const publish = async () => {
    if (!classId) { toast.error('Pick a class to assign this quiz to'); return; }
    setPublishing(true);
    try {
      if (examMode) {
        // Must also pass `questions` (type per question, no negative_marks)
        // so the backend fills in the real JEE/NEET default magnitude
        // (0.25 / 1.0) per question — exam_mode alone does NOT retroactively
        // set negative_marks on the already-generated questions.
        await axios.put(`${API}/quizzes/${quiz.id}/exam-settings`, {
          exam_mode: examMode,
          questions: quiz.questions.map(q => ({ question_number: q.question_number, type: q.type })),
        });
      }
      const updates = { class_id: classId };
      if (timeLimit) updates.time_limit = Number(timeLimit);
      await axios.put(`${API}/quizzes/${quiz.id}`, updates);
      await axios.post(`${API}/quizzes/${quiz.id}/publish`);
      setPublished(true);
      toast.success('Quiz published — students in this class can attend it now.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not publish quiz');
    } finally {
      setPublishing(false);
    }
  };

  const reset = () => {
    setQuiz(null); setClassId(''); setExamMode(''); setTimeLimit(''); setPublished(false);
  };

  return (
    <DashboardShell title="Quiz Generator">
      <div className="space-y-6 max-w-3xl" data-testid="teacher-quiz-generator">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Quiz Generator</h1>
            <p className="text-sm text-muted-foreground">Generate a quiz, optionally set it to JEE/NEET exam mode, and publish it to a class.</p>
          </div>
        </div>

        {!quiz && (
          <Card>
            <CardHeader><CardTitle>1. Generate</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Topic</Label>
                <Input value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                  placeholder="e.g. Laws of Motion" data-testid="quiz-topic-input" />
              </div>
              <div>
                <Label>Context (optional — pasted notes, chapter summary, etc.)</Label>
                <textarea
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                  rows={4}
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  data-testid="quiz-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Difficulty</Label>
                  <select className="w-full rounded-lg border px-3 py-2 text-sm" value={form.difficulty}
                    onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))} data-testid="quiz-difficulty-select">
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <Label>Number of questions</Label>
                  <Input type="number" min={1} max={20} value={form.count}
                    onChange={e => setForm(f => ({ ...f, count: Number(e.target.value) }))} data-testid="quiz-count-input" />
                </div>
              </div>
              <Button onClick={generate} disabled={generating} data-testid="quiz-generate-button">
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate quiz</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {quiz && !published && (() => {
          const excludedTypes = [...new Set(
            quiz.questions.filter(q => !EXAM_MODE_GRADABLE_TYPES.has(q.type)).map(q => q.type)
          )];
          const excludedCount = quiz.questions.filter(q => !EXAM_MODE_GRADABLE_TYPES.has(q.type)).length;
          return (
          <>
            <Card>
              <CardHeader><CardTitle>2. Review — {quiz.title}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {quiz.questions.map((q, i) => (
                  <div key={q.question_number} className="rounded-lg border p-3 text-sm" data-testid={`quiz-review-question-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline">{i + 1}</Badge>
                      <Badge variant="secondary" className="capitalize">{q.type}</Badge>
                    </div>
                    <div className="font-medium">{q.question}</div>
                    {q.options && (
                      <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                        {q.options.map((opt, j) => <li key={j}>{opt}{opt === q.correct_answer ? ' (correct)' : ''}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>3. Assign &amp; exam settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Class</Label>
                  <select className="w-full rounded-lg border px-3 py-2 text-sm" value={classId}
                    onChange={e => setClassId(e.target.value)} data-testid="quiz-class-select">
                    <option value="">Select a class…</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Exam mode</Label>
                  <select className="w-full rounded-lg border px-3 py-2 text-sm" value={examMode}
                    onChange={e => setExamMode(e.target.value)} data-testid="quiz-exam-mode-select">
                    {EXAM_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  {examMode && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {NO_NEGATIVE_MARKING_EXAMS.has(examMode)
                        ? `${examMode} has no negative marking — wrong answers score the same as unanswered ones.`
                        : `Negative marking applies to ${[...NEGATIVE_MARKING_TYPES].join(' and ')} questions at the standard ${examMode === 'NEET' ? '1.0' : '0.25'}-mark default.`}
                    </p>
                  )}
                  {examMode && excludedCount > 0 && (
                    <div className="flex items-start gap-2 mt-2 p-2.5 rounded-lg bg-amber-50 text-amber-800 text-xs" data-testid="quiz-exam-mode-exclusion-warning">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{excludedCount} question{excludedCount === 1 ? '' : 's'} ({excludedTypes.join(', ')}) won&rsquo;t appear to students — exam mode only supports MCQ, numerical, and assertion-reason questions.</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Time limit (minutes, optional)</Label>
                  <Input type="number" min={1} value={timeLimit} onChange={e => setTimeLimit(e.target.value)} data-testid="quiz-time-limit-input" />
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={reset} data-testid="quiz-discard-button">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Start over
                  </Button>
                  <Button onClick={publish} disabled={publishing} data-testid="quiz-publish-button">
                    {publishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</> : <><Award className="w-4 h-4 mr-2" /> Publish to class</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
          );
        })()}

        {published && (
          <Card>
            <CardContent className="p-8 text-center space-y-3">
              <Award className="w-10 h-10 mx-auto text-primary" />
              <p className="font-semibold">Quiz published</p>
              <p className="text-sm text-muted-foreground">Students in this class will see it under Exam Prep → My Quizzes.</p>
              <Button onClick={reset} data-testid="quiz-create-another-button">Create another</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardShell>
  );
}
